import { useEffect, useState, type ReactNode } from "react";
import { api, ApiError, type ResumoGerenciarOs } from "../api";
import { formatDate } from "../format";
import { IconAlert, IconClose, IconEdit, IconTrash } from "../icons";

/**
 * EDITAR E EXCLUIR A OS INTEIRA (e excluir uma amostra) — os pop-ups usados
 * pelo Protocolo e pelas Análises. Ver server/os-gerenciar.ts.
 *
 * Os dois pop-ups da OS começam lendo o RETRATO dela no servidor, e não o que
 * a tela de quem abriu tem na mão: a lista das Análises só conhece o ensaio da
 * aba, e a exclusão precisa mostrar TUDO o que vai junto (as amostras de todos
 * os ensaios, a conferência, as pendências, a liberação, a remessa).
 */

const ROTULO_CAMPO: Record<string, string> = {
  cliente: "o cliente",
  solicitante: "o solicitante",
  prazo_retorno: "o prazo de retorno",
  prazo_coleta: "o prazo de coleta",
};

/** "a, b e c" */
function juntar(itens: string[]): string {
  if (itens.length <= 1) return itens.join("");
  return `${itens.slice(0, -1).join(", ")} e ${itens.at(-1)}`;
}

/** Moldura comum: cabeçalho com título, corpo e rodapé com os botões. */
function Moldura({
  titulo,
  subtitulo,
  onCancel,
  fecharNoFundo = true,
  rodape,
  children,
}: {
  titulo: string;
  subtitulo?: string;
  onCancel: () => void;
  /** Formulário com texto digitado não fecha no clique fora (perderia o que foi escrito). */
  fecharNoFundo?: boolean;
  rodape: ReactNode;
  children: ReactNode;
}) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onCancel]);

  return (
    <div className="modal-overlay" onMouseDown={fecharNoFundo ? onCancel : undefined}>
      <div className="modal-card prot-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>{titulo}</h3>
            {subtitulo && <p className="rf-head-sub">{subtitulo}</p>}
          </div>
          <button className="icon-btn" onClick={onCancel} title="Fechar">
            <IconClose size={18} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-foot">{rodape}</div>
      </div>
    </div>
  );
}

/** Carrega o retrato da OS (dados gerais + o que cai junto numa exclusão). */
function useResumoOs(os: string) {
  const [resumo, setResumo] = useState<ResumoGerenciarOs | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    setResumo(null);
    setErro(null);
    api.protocolo
      .gerenciar(os)
      .then((r) => vivo && setResumo(r))
      .catch((e) => vivo && setErro(e instanceof ApiError ? e.message : "Não foi possível abrir a OS."));
    return () => {
      vivo = false;
    };
  }, [os]);
  return { resumo, erro };
}

/* ======================================================================== */
/*  EDITAR A OS                                                              */
/* ======================================================================== */

export function ModalEditarOs({
  os,
  onCancel,
  onSalvo,
}: {
  os: string;
  onCancel: () => void;
  /** `novoNumero` = o número depois de salvar (muda quando a OS foi renumerada). */
  onSalvo: (novoNumero: string, mensagem: string) => void;
}) {
  const { resumo, erro: erroCarga } = useResumoOs(os);
  const [numero, setNumero] = useState(os);
  const [cliente, setCliente] = useState("");
  const [solicitante, setSolicitante] = useState("");
  const [prazoColeta, setPrazoColeta] = useState("");
  const [prazoRetorno, setPrazoRetorno] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Preenche o formulário quando o retrato chega.
  useEffect(() => {
    if (!resumo) return;
    setNumero(resumo.os_numero);
    setCliente(resumo.cliente ?? "");
    setSolicitante(resumo.solicitante ?? "");
    setPrazoColeta(resumo.prazo_coleta ?? "");
    setPrazoRetorno(resumo.prazo_retorno ?? "");
  }, [resumo]);

  async function salvar() {
    if (!resumo) return;
    setErro(null);
    if (!numero.trim()) return setErro("Informe o número da OS.");
    if (!cliente.trim()) return setErro("Informe o cliente.");
    /*
     * SÓ O QUE MUDOU vai para o servidor. Um campo em que as amostras
     * discordam e que a pessoa não tocou continua como está em cada uma —
     * igualar todas sem ela ter pedido seria apagar dado por conta própria.
     */
    const mudou: Parameters<typeof api.protocolo.editarOs>[1] = {};
    if (numero.trim() !== resumo.os_numero) mudou.os_numero = numero.trim();
    if (cliente.trim() !== (resumo.cliente ?? "")) mudou.cliente = cliente.trim();
    if (solicitante.trim() !== (resumo.solicitante ?? "")) mudou.solicitante = solicitante.trim();
    if (prazoColeta !== (resumo.prazo_coleta ?? "")) mudou.prazo_coleta = prazoColeta;
    if (prazoRetorno !== (resumo.prazo_retorno ?? "")) mudou.prazo_retorno = prazoRetorno;
    if (Object.keys(mudou).length === 0) {
      onCancel();
      return;
    }
    setSalvando(true);
    try {
      const r = await api.protocolo.editarOs(resumo.os_numero, mudou);
      onSalvo(
        r.os_numero,
        r.os_numero !== resumo.os_numero
          ? `OS ${resumo.os_numero} agora é OS ${r.os_numero} (${r.amostras} amostra(s)).`
          : `Dados da OS ${r.os_numero} salvos em ${r.amostras} amostra(s).`,
      );
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível salvar.");
      setSalvando(false);
    }
  }

  return (
    <Moldura
      titulo={`Editar OS ${os}`}
      subtitulo={
        resumo
          ? `Os dados gerais valem para as ${resumo.amostras} amostra(s) da OS de uma vez.`
          : "Carregando a OS..."
      }
      onCancel={onCancel}
      fecharNoFundo={false}
      rodape={
        <>
          {(erro || erroCarga) && <div className="form-error">{erro || erroCarga}</div>}
          <div className="modal-foot-actions">
            <button className="btn-secondary" onClick={onCancel}>
              Cancelar
            </button>
            <button className="btn-primary" onClick={() => void salvar()} disabled={!resumo || salvando}>
              <IconEdit size={16} /> {salvando ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </>
      }
    >
      {!resumo ? (
        <p className="prot-nota">{erroCarga ? "" : "Carregando..."}</p>
      ) : (
        <div className="form-grid">
          <label className="field field-wide">
            <span>Número da OS / protocolo</span>
            <input
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              disabled={!!resumo.numero_travado}
              autoFocus={!resumo.numero_travado}
            />
            {resumo.numero_travado && <em className="rf-ajuda">{resumo.numero_travado}</em>}
          </label>
          <label className="field field-wide">
            <span>Cliente</span>
            <input value={cliente} onChange={(e) => setCliente(e.target.value)} autoFocus={!!resumo.numero_travado} />
          </label>
          <label className="field field-wide">
            <span>Solicitante</span>
            <input
              value={solicitante}
              onChange={(e) => setSolicitante(e.target.value)}
              placeholder="quem pediu a análise"
            />
          </label>
          <label className="field">
            <span>Prazo de coleta</span>
            <input type="date" value={prazoColeta} onChange={(e) => setPrazoColeta(e.target.value)} />
          </label>
          <label className="field">
            <span>Prazo de retorno</span>
            <input type="date" value={prazoRetorno} onChange={(e) => setPrazoRetorno(e.target.value)} />
          </label>
          {resumo.divergentes.length > 0 && (
            <p className="field-wide prot-nota">
              As amostras desta OS estão com {juntar(resumo.divergentes.map((c) => ROTULO_CAMPO[c] ?? c))}{" "}
              diferentes entre si (o formulário mostra o mais comum). O campo que você mudar passa a valer para todas.
            </p>
          )}
          <p className="field-wide prot-nota">
            Dados de cada amostra (nº, coleta, equipamento, ensaios) continuam no lápis da linha da amostra.
          </p>
        </div>
      )}
    </Moldura>
  );
}

/* ======================================================================== */
/*  EXCLUIR A OS                                                             */
/* ======================================================================== */

export function ModalExcluirOs({
  os,
  onCancel,
  onExcluida,
}: {
  os: string;
  onCancel: () => void;
  onExcluida: (mensagem: string) => void;
}) {
  const { resumo, erro: erroCarga } = useResumoOs(os);
  const [digitado, setDigitado] = useState("");
  const [comRemessa, setComRemessa] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const confere = digitado.trim() === os.trim();

  async function excluir() {
    if (!resumo || !confere) return;
    setErro(null);
    setExcluindo(true);
    try {
      const r = await api.protocolo.excluirOs(resumo.os_numero, { remessa: comRemessa, confirmar: digitado.trim() });
      onExcluida(
        `OS ${r.os_numero} excluída (${r.amostras} amostra(s)` +
          (r.remessas > 0 ? `, ${r.remessas} remessa(s) de material` : "") +
          ").",
      );
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível excluir a OS.");
      setExcluindo(false);
    }
  }

  const lista = resumo?.amostras_lista ?? [];
  const MOSTRAR = 8;

  return (
    <Moldura
      titulo={`Excluir OS ${os}`}
      subtitulo="Apaga a OS do laboratório. Não pode ser desfeito."
      onCancel={onCancel}
      fecharNoFundo={false}
      rodape={
        <>
          {(erro || erroCarga) && <div className="form-error">{erro || erroCarga}</div>}
          <div className="modal-foot-actions">
            <button className="btn-secondary" onClick={onCancel}>
              Cancelar
            </button>
            <button className="btn-perigo" onClick={() => void excluir()} disabled={!resumo || !confere || excluindo}>
              <IconTrash size={16} /> {excluindo ? "Excluindo..." : "Excluir OS"}
            </button>
          </div>
        </>
      }
    >
      {!resumo ? (
        <p className="prot-nota">{erroCarga ? "" : "Carregando..."}</p>
      ) : (
        <div className="osg-corpo">
          <p className="osg-texto">
            Sai junto, com tudo o que foi lançado em cada uma (resultados, conclusões, pareceres, aprovações e
            laudos):
          </p>
          <ul className="osg-lista">
            <li>
              <strong>{resumo.amostras} amostra(s)</strong>
              {lista.length > 0 && (
                <span className="osg-nomes">
                  {lista
                    .slice(0, MOSTRAR)
                    .map((a) => a.nome)
                    .join(" · ")}
                  {lista.length > MOSTRAR ? ` · e mais ${lista.length - MOSTRAR}` : ""}
                </span>
              )}
            </li>
            {resumo.recebimentos > 0 && <li>{resumo.recebimentos} conferência(s) de entrada no laboratório</li>}
            {resumo.pendencias.total > 0 && (
              <li>
                {resumo.pendencias.total} pendência(s) da OS
                {resumo.pendencias.abertas > 0 ? ` (${resumo.pendencias.abertas} em aberto)` : ""}
              </li>
            )}
            {resumo.liberada && (
              <li>
                a liberação ao cliente de {formatDate(resumo.liberada.em)}
                {resumo.liberada.por ? ` (${resumo.liberada.por})` : ""}
              </li>
            )}
          </ul>

          {(resumo.com_trabalho > 0 || resumo.entregues > 0) && (
            <div className="osg-avisos">
              {resumo.com_trabalho > 0 && (
                <p className="prot-aviso">
                  <IconAlert size={14} /> {resumo.com_trabalho} amostra(s) já têm resultado, conclusão ou parecer — esse
                  trabalho se perde.
                </p>
              )}
              {resumo.entregues > 0 && (
                <p className="prot-aviso">
                  <IconAlert size={14} /> {resumo.entregues} amostra(s) já tiveram o laudo entregue ao cliente — a cópia
                  guardada aqui também sai.
                </p>
              )}
            </div>
          )}

          {resumo.origens.includes("campo") && (
            <p className="prot-nota">
              Esta OS veio do campo (Relatórios). Lá ela continua existindo; se o Relatórios mandar a solicitação de
              novo, ela volta a aparecer aqui.
            </p>
          )}
          {resumo.origens.includes("planejamento") && (
            <p className="prot-nota">
              Esta OS veio do Planejamento. Lá ela continua existindo; se for enviada de novo ao laboratório, volta a
              aparecer aqui.
            </p>
          )}

          {resumo.remessa && (
            <label className="prot-check osg-remessa">
              <input type="checkbox" checked={comRemessa} onChange={(e) => setComRemessa(e.target.checked)} />
              <span>
                Excluir também a remessa de material desta OS (aba Material do cliente
                {resumo.remessa.bipados > 0 ? ` — ${resumo.remessa.bipados} frasco(s) conferido(s)` : ""}). Sem marcar,
                ela continua lá.
              </span>
            </label>
          )}

          <label className="field osg-confirmar">
            <span>
              Para confirmar, digite o número da OS: <strong className="osg-num">{os}</strong>
            </span>
            <input
              value={digitado}
              onChange={(e) => setDigitado(e.target.value)}
              autoFocus
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === "Enter" && confere) void excluir();
              }}
            />
          </label>
        </div>
      )}
    </Moldura>
  );
}

/* ======================================================================== */
/*  EXCLUIR UMA AMOSTRA                                                      */
/* ======================================================================== */

export interface AmostraParaExcluir {
  id: number;
  nome: string;
  os: string;
  /** O laudo já foi entregue ao cliente. */
  entregue: boolean;
  /** Há resultado lançado, ensaio concluído ou parecer. */
  comTrabalho: boolean;
  /** É a única amostra da OS (a OS some junto da lista). */
  ultima: boolean;
}

export function ModalExcluirAmostra({
  amostra,
  onCancel,
  onExcluida,
}: {
  amostra: AmostraParaExcluir;
  onCancel: () => void;
  onExcluida: (mensagem: string, ultimaDaOs: boolean) => void;
}) {
  const [excluindo, setExcluindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function excluir() {
    setErro(null);
    setExcluindo(true);
    try {
      const r = await api.protocolo.excluirAmostra(amostra.id);
      onExcluida(
        r.ultima_da_os
          ? `Amostra ${amostra.nome} excluída — era a última da OS ${amostra.os}, que saiu da lista.`
          : `Amostra ${amostra.nome} excluída da OS ${amostra.os}.`,
        r.ultima_da_os,
      );
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível excluir a amostra.");
      setExcluindo(false);
    }
  }

  return (
    <Moldura
      titulo="Excluir amostra"
      subtitulo={`${amostra.nome} · OS ${amostra.os}`}
      onCancel={onCancel}
      rodape={
        <>
          {erro && <div className="form-error">{erro}</div>}
          <div className="modal-foot-actions">
            <button className="btn-secondary" onClick={onCancel} autoFocus>
              Cancelar
            </button>
            <button className="btn-perigo" onClick={() => void excluir()} disabled={excluindo}>
              <IconTrash size={16} /> {excluindo ? "Excluindo..." : "Excluir amostra"}
            </button>
          </div>
        </>
      }
    >
      <div className="osg-corpo">
        <p className="osg-texto">
          A amostra sai do laboratório com tudo o que foi lançado nela. Não pode ser desfeito.
        </p>
        {(amostra.comTrabalho || amostra.entregue || amostra.ultima) && (
          <div className="osg-avisos">
            {amostra.comTrabalho && (
              <p className="prot-aviso">
                <IconAlert size={14} /> Ela já tem resultado, conclusão ou parecer — esse trabalho se perde.
              </p>
            )}
            {amostra.entregue && (
              <p className="prot-aviso">
                <IconAlert size={14} /> O laudo dela já foi entregue ao cliente — a cópia guardada aqui também sai.
              </p>
            )}
            {amostra.ultima && (
              <p className="prot-aviso">
                <IconAlert size={14} /> É a única amostra da OS {amostra.os}: a OS inteira sai da lista.
              </p>
            )}
          </div>
        )}
      </div>
    </Moldura>
  );
}
